import { AfterViewInit, Component,OnDestroy, OnInit } from '@angular/core';
import { UntypedFormArray} from '@angular/forms';
import { FormBroadcast, FormComponentLookup } from '@ptl/forms';
import { Observable, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { PersonFormAsJson, IndividualMarketPersonsComponent } from '../individual-market-persons.component';
import { select } from '@ptl/angular-redux-store';
import { Person } from '../interfaces/person.interface';
import { GlobalDataStateStructure } from 'src/app/angular-elements/shared/components/configs/interfaces/globalStateStructure.interface';
import { saveMemberID } from 'src/app/angular-elements/shared/components/configs/reducers/globalData.reducer';

@FormComponentLookup('dependents-person')
@Component({
    selector: 'app-dependents',
    templateUrl: './../individual-market-persons.component.html',
    styleUrls: ['./../individual-market-persons.component.scss'],
    standalone: false
})
export class DependentsComponent extends IndividualMarketPersonsComponent
  implements OnInit, AfterViewInit, OnDestroy {

  personsArrayChanged: Subscription;
  effectiveDateChanged: Subscription;
  @select(['payloadStates', 'individualMarket', 'childOnlyPolicy']) childOnlyChanges$: Observable<'Yes' | 'No'>;
  childOnlyChanged: Subscription;
  isChildOnly: boolean;

  @select(['payloadStates', 'individualMarket', 'ratingInfo', 'dependents']) dependentsChanges$: Observable<any>;
  dependentsChanged: Subscription;

  private copyDependent: Person[];

  addMoreButtonContainer: HTMLDivElement;
  interactableDiv: HTMLDivElement;
  messageDiv: HTMLDivElement;

  constructor() {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
    this.applicantType = 'Dependent';

    /** Watch for form changes, update children controls as appropriate */
    this.personsArrayChanged = this.personsForm.valueChanges
      .pipe(debounceTime(300))
      .subscribe((val: PersonFormAsJson) => {
        this.updateAddMoreDependentsButton();
      });

    // Update all dependent dob's validity when effective date changes because their validation uses this value
    this.effectiveDateChanged = this.parentForm["individualMarket.ratingInfo"].controls.effectiveDate.valueChanges.subscribe((newVal) => {
      if (this.personsForm?.controls?.persons?.['controls']?.length) {
        this.personsForm.controls.persons['controls'].forEach((formGroup) => {
          formGroup.controls.dob.updateValueAndValidity();
        });
      }
    });

    /**
     * Watch for child-only changes
     * update ability to add more dependents and
     * to remove dependents as appropriate
     */
    this.childOnlyChanged = this.childOnlyChanges$.subscribe(x => this.onChildOnlyChanges(x));

    /** Check initial form status - update children controls as appropriate */
    this.updateAddMoreDependentsButton();
    this.updateTobaccoIndicatorControlAvailability();

    if (this.entry.custom?.nonSubscriberPersonsInclusiveLimit) {
      this.dependentsChanged = this.dependentsChanges$.subscribe(val => {
        this.onDependentsChanges();
      });

      this.spousalPartnerChanged = this.spousalPartnerChanges$.subscribe(val => {
        this.onSpousalPartnerChanges();
      });
    }
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();

    /** Create and interactable container */
    this.addMoreButtonContainer = this.renderer.createElement('div');

    this.interactableDiv = this.renderer.createElement('div');
    this.interactableDiv.style.marginTop = '15px';

    const anchor: HTMLAnchorElement = this.renderer.createElement('a');
    anchor.href = 'javascript:void(0)';
    anchor.style.display = 'flex';
    anchor.style.textDecoration = 'none';
    anchor.style.color = 'inherit';
    anchor.setAttribute('id', 'addDependent');

    /** Icon */
    const iconSpan: HTMLSpanElement = this.renderer.createElement('span');
    iconSpan.classList.add('material-symbols-outlined');
    iconSpan.style.verticalAlign = 'middle';
    iconSpan.innerHTML = 'control_point';

    /** Content */
    const contentSpan: HTMLSpanElement = this.renderer.createElement('span');
    contentSpan.style.display = 'inline-block';
    contentSpan.style.margin = '-2px 0px 0px 5px';
    contentSpan.innerHTML = 'Add Dependent';

    /** Attach */
    anchor.appendChild(iconSpan);
    anchor.appendChild(contentSpan);

    this.interactableDiv.appendChild(anchor);

    this.addMoreButtonContainer.appendChild(this.interactableDiv);

    (this.templateCustom.nativeElement as HTMLElement).append(
      this.addMoreButtonContainer
    );

    /** Apply onClick for adding more dependents */
    this.interactableDiv.onclick = evt => {
      this.personsFormArray.push(this.newPersonData());
      setTimeout(() => {
        const lastRemoveLink = this.removeLink.last;
        if(lastRemoveLink){
          lastRemoveLink.nativeElement.focus();
        }
      }, 100);
      this.announceMessage('Dependent Details populated, please fill it');
    };

    this.getDependentLimitMessage();

    this.updateAddMoreDependentsButton();
  }

  removePerson(index: number) {
    (this.personsForm.get('persons') as UntypedFormArray).removeAt(index);
    let totalPersons = (this.personsForm.get('persons') as UntypedFormArray).length;
    let ind = (totalPersons > 0) ? ' ' + (index + 1) : '';
    this.announceMessage(this.applicantType + ind + ' details deleted');
    if (totalPersons == index) {
      document.getElementById('addDependent').focus();
    } else {
      setTimeout(() => {
        const focusRemoveLink = this.removeLink.get(index);
        if (focusRemoveLink) {
          focusRemoveLink.nativeElement.focus();
        }
      }, 100);
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();

    if (this.personsArrayChanged) {
      this.personsArrayChanged.unsubscribe();
    }
    if (this.effectiveDateChanged) {
      this.effectiveDateChanged.unsubscribe();
    }
  }

  getDependentLimitMessage() {
    this.messageDiv = this.renderer.createElement('div');
    this.messageDiv.style.display = 'none';
    this.messageDiv.style.marginTop = '15px';

    /** Content */
    const messageContentSpan: HTMLSpanElement = this.renderer.createElement('span');
    messageContentSpan.style.display = 'inline-block';
    messageContentSpan.style.margin = '-2px 0px 0px 5px';
    messageContentSpan.innerHTML = this.entry.custom?.maxDependentsAndSpouseMessage || "";

    this.messageDiv.appendChild(messageContentSpan);

    this.addMoreButtonContainer.appendChild(this.messageDiv);
  }

  onChildOnlyChanges(val: 'Yes' | 'No') {
    const globalData: GlobalDataStateStructure = this.reducerUtilityService.getComponentStates().GlobalData;
    this.isChildOnly = val && val.toUpperCase() === 'YES';
    if (!this.copyDependent) {
      this.copyDependent = Object.assign([], this.personsFormArray.value);
    }
    this.personsFormArray.clear();
    if (this.isChildOnly) {
      setTimeout(() => {
        if(globalData.findMemberId){
          this.personsFormArray.push(this.newPersonData(this.copyDependent[0]));
        }else{
          this.personsFormArray.push(this.newPersonData());
        }
      }, 50)
    } else {
      if (this.copyDependent && this.copyDependent.length > 0) {
        this.copyDependent.forEach(person => { this.personsFormArray.push(this.newPersonData(person)); });
      }
    }
    this.ngRedux.dispatch(saveMemberID(""));
    this.allowRemoval = !this.isChildOnly ? true : false;
    this.updateAddMoreDependentsButton();
  }

  async updateAddMoreDependentsButton(): Promise<void> {
    const atMax = await this.atMax();
    const payload = this.reducerUtilityService.getPayloadStates();
    const spouseFromPayload: { [key: string]: any } = payload?.["individualMarket"]?.["ratingInfo"]?.["spousal-partner"];
    const dependentsFromPayload: { [key: string]: any } = payload?.["individualMarket"]?.["ratingInfo"]?.["dependents"];

    if (this.addMoreButtonContainer) {
      const spouseLength = (spouseFromPayload === undefined) ? 0 : Object?.keys(spouseFromPayload).length;
      const dependentLength = (dependentsFromPayload === undefined) ? 0 : Object?.keys(dependentsFromPayload).length;
      const atSpouseDependentMax = (spouseLength + dependentLength) === this.entry.custom?.nonSubscriberPersonsInclusiveLimit
      if ((atMax || atSpouseDependentMax) && !this.isChildOnly) {
        this.messageDiv.style.display = 'flex';
      } else {
        this.messageDiv.style.display = 'none';
      }

      if ((atMax || atSpouseDependentMax) || this.isChildOnly) {
        this.interactableDiv.style.display = 'none';
      } else {
        this.interactableDiv.style.display = '';
      }

    }
  }

  /**
   * Waits for form-listeners to complete, and then returns a comparison of
   * whether the configured maxDependent value is high-enough to add more dependents
   */
  atMax(): Promise<boolean> {
    const deferred = new Promise<boolean>((resolve, reject) => {
      setTimeout(() => {
        const maxDependents = (this.personsFormArray.length >= (this.entry.custom && this.entry.custom.maxDependents));
        resolve(maxDependents);
      }, 20);
    });
    return deferred;
  }

  get personsFormArray(): UntypedFormArray {
    return this.personsForm.get('persons') as UntypedFormArray;
  }

  onDependentsChanges() {
    const payload = this.reducerUtilityService.getPayloadStates();
    const dependentsFromPayload: { [key: string]: any } = payload?.["individualMarket"]?.["ratingInfo"]?.["dependents"];
    
    if (dependentsFromPayload) {
      const dependentLength = Object.keys(dependentsFromPayload).length;

      var event: FormBroadcast = {
        formControlName: `${this.entry.custom.customFormControlName}`,
        type: 'valueChange',
        value: dependentLength
      };

      this.formStreamTrigger.triggerFormStream(event);
    }

    this.updateAddMoreDependentsButton();
  }

  onSpousalPartnerChanges() {
    this.updateAddMoreDependentsButton();
  }

}
